import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logoLight from "../../assets/logo1.png";
import hiklogo from "../../assets/hikvision.png";
import dahuaLogo from "../../assets/dahuaLogo.png";
import zktlogo from "../../assets/zkteco.png";
import { QRCodeCanvas } from "qrcode.react";
import { 
    FiChevronLeft, 
    FiCheckCircle, 
    FiAlertCircle, 
    FiSearch, 
    FiFileText 
} from "react-icons/fi";

export default function EbarimtScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Өмнөх дэлгэцээс дамжиж ирсэн мэдээллүүд
    const parkId = location.state?.parkId || "default_park_id";
    const price = location.state?.price || 0; // Display only; server verifies amount.
    const recordId = location.state?.recordId || null;
    const PAYMENT_API = (process.env.REACT_APP_PAYMENT_API_BASE ||
        "https://us-central1-iparking-55511.cloudfunctions.net").replace(/\/$/, "");
    const title = location.state?.title || "iParking";

    // Төлөвүүд (States)
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isReceiptGenerated, setIsReceiptGenerated] = useState(false);
    
    const [isOrganization, setIsOrganization] = useState(false);
    const [regNo, setRegNo] = useState("");
    const [isFetchingOrg, setIsFetchingOrg] = useState(false);
    const [orgName, setOrgName] = useState("");
    const [orgError, setOrgError] = useState("");

    const [serverAmount, setServerAmount] = useState(null);
    const [receiptData, setReceiptData] = useState({ date: "", qrData: "", billId: "", lottery: "" });
    const [toastMsg, setToastMsg] = useState("");

    // Never read encryptKey/encryptIV into the browser. This page can only
    // request a server-issued receipt for a verified paid parking record.
    const fetchConfig = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);
        if (!parkId || !recordId) setHasError(true);
        setIsLoading(false);
    }, [parkId, recordId]);
    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    // Түр зуурын алдааны мессеж харуулах (Toast)
    const showToast = (msg) => {
        setToastMsg(msg);
        setTimeout(() => setToastMsg(""), 3000);
    };

    // 2. Verify organization via our server-side proxy (no direct browser CORS).
    const checkOrgName = async () => {
        const trimmedReg = regNo.trim().toUpperCase();
        if (!/^[0-9A-ZА-ЯЁӨҮ]{5,20}$/.test(trimmedReg)) {
            setOrgError("Байгууллагын регистр буруу байна."); return;
        }
        setIsFetchingOrg(true); setOrgError(""); setOrgName("");
        try {
            const response = await fetch(
                "https://asia-northeast1-iparking-55511.cloudfunctions.net/getEbarimtMerchantInfoAsia",
                { method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({regNo: trimmedReg}) }
            );
            const data = await response.json();
            const name = data?.name || data?.data?.name || "";
            if (!response.ok || !name) throw new Error(data.message || "Байгууллага олдсонгүй.");
            setOrgName(name);
        } catch (error) { setOrgError(error.message || "Байгууллага шалгах боломжгүй."); }
        finally { setIsFetchingOrg(false); }
    };

    const getEbarimt = async () => {
        if (isLoading || isReceiptGenerated) return;
        if (!recordId) return showToast("Баталгаажсан төлбөрийн бүртгэл олдсонгүй.");
        if (isOrganization && !orgName) return showToast("Эхлээд байгууллагаа шалгана уу.");
        setIsLoading(true); setHasError(false);
        try {
            const response = await fetch(`${PAYMENT_API}/requestPaidParkingEbarimt`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ parkId, recordId,
                    billType: isOrganization ? "3" : "1",
                    customerNo: isOrganization ? regNo.trim().toUpperCase() : "" }),
            });
            const result = await response.json();
            if (!response.ok || !result.ok) throw new Error(result.message || "И-Баримтын серверийн алдаа.");
            const receipt = result.receipt || {};
            setServerAmount(result.amount);
            setReceiptData({ date: receipt.date || "—", qrData: receipt.qrData || "",
                billId: receipt.billId || "", lottery: receipt.lottery || "" });
            setIsReceiptGenerated(true);
        } catch (error) {
            setHasError(true);
            showToast(`${error.message || "И-Баримт үүсгэхэд алдаа гарлаа."} Давтан төлөхгүйгээр төлөвөө шалгана уу.`);
        } finally { setIsLoading(false); }
    };

    // --- UI БҮРЭЛДЭХҮҮН ХЭСГҮҮД ---

    const UI_FONT = '"Noto Sans", Inter, "Segoe UI", Arial, sans-serif';
    const TITLE_FONT = '"Arial", "Noto Sans", "Segoe UI", sans-serif';
    const formatPrice = (value) => `${Number(value || 0).toLocaleString('mn-MN')}₮`;

    const PartnerStrip = () => (
        <div className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-border-t tw-border-white/[0.07] tw-pt-3">
            <img src={hiklogo} alt="Hikvision" className="tw-h-[14px] tw-w-[48px] tw-object-contain tw-opacity-60" />
            <span className="tw-h-3 tw-w-px tw-bg-white/10" />
            <img src={dahuaLogo} alt="Dahua" className="tw-h-[14px] tw-w-[46px] tw-object-contain tw-opacity-60" />
            <span className="tw-h-3 tw-w-px tw-bg-white/10" />
            <img src={zktlogo} alt="ZKTeco" className="tw-h-[14px] tw-w-[44px] tw-object-contain tw-opacity-60" />
        </div>
    );

    const Footer = () => (
        <div className="tw-mt-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-[7.5px] tw-font-medium tw-tracking-[0.03em] tw-text-slate-400">
            <span>iParking</span>
            <span className="tw-h-1 tw-w-1 tw-rounded-full tw-bg-slate-300" />
            <span>Smart Parking System</span>
        </div>
    );

    const Header = ({ badge = "И-Баримт" }) => (
        <header className="tw-bg-[#0B1220] tw-px-5 tw-pt-4 tw-pb-5 min-[390px]:tw-px-6">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <img src={logoLight} alt="iParking" className="tw-w-[100px] tw-object-contain" />
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-emerald-400/15 tw-bg-emerald-400/[0.07] tw-px-2.5 tw-py-1.5">
                    <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-400" />
                    <span className="tw-text-[8.5px] tw-font-semibold tw-text-emerald-200">{badge}</span>
                </div>
            </div>

            <div className="tw-mt-5 tw-text-center">
                <h1
                    className="tw-m-0 tw-text-[24px] min-[390px]:tw-text-[26px] tw-font-normal tw-leading-[1.18] tw-tracking-[-0.02em] tw-text-white"
                    style={{ fontFamily: TITLE_FONT }}
                >
                    E-BARIMT
                </h1>
                <p className="tw-mx-auto tw-mt-2 tw-mb-0 tw-max-w-[285px] tw-text-[10.5px] tw-leading-[1.6] tw-text-slate-400">
                    Худалдан авалтын баримт
                </p>
            </div>

            <PartnerStrip />
        </header>
    );

    const renderLoading = () => (
        <div className="tw-flex tw-min-h-[300px] tw-flex-col tw-items-center tw-justify-center tw-text-center">
            <div className="tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-[3px] tw-border-slate-200 tw-border-t-[#16836F]" />
            <h3 className="tw-mt-4 tw-mb-0 tw-text-[16px] tw-font-semibold tw-text-slate-900">Мэдээлэл уншиж байна</h3>
            <p className="tw-mt-1.5 tw-mb-0 tw-text-[10.5px] tw-text-slate-500">Түр хүлээнэ үү</p>
        </div>
    );

    const renderError = () => (
        <div className="tw-flex tw-min-h-[300px] tw-flex-col tw-items-center tw-justify-center tw-text-center">
            <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-rose-50 tw-text-rose-500">
                <FiAlertCircle size={26} />
            </div>
            <h3 className="tw-mt-4 tw-mb-0 tw-text-[16px] tw-font-semibold tw-text-slate-900">Алдаа гарлаа</h3>
            <p className="tw-mx-auto tw-mt-1.5 tw-mb-0 tw-max-w-[280px] tw-text-[10.5px] tw-leading-[1.6] tw-text-slate-500">
                И-Баримтын тохиргоо эсвэл сүлжээний холболтыг шалгана уу.
            </p>
            <button
                type="button"
                onClick={fetchConfig}
                className="tw-mt-5 tw-h-11 tw-w-full tw-rounded-[13px] tw-bg-[#0B1220] tw-text-[11px] tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#162033] active:tw-scale-[0.99]"
            >
                Дахин оролдох
            </button>
        </div>
    );

    const renderSelection = () => (
        <div className="tw-w-full">
            <div className="tw-rounded-[19px] tw-border tw-border-slate-200/90 tw-bg-white tw-p-4 min-[390px]:tw-p-[18px]">
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                    <div>
                        <span className="tw-block tw-text-[9px] tw-font-medium tw-uppercase tw-tracking-[0.08em] tw-text-slate-400">Төлбөрийн дүн</span>
                        <span className="tw-mt-1 tw-block tw-text-[26px] tw-font-semibold tw-tracking-[-0.035em] tw-text-[#101828]">
                            {formatPrice(serverAmount ?? price)}
                        </span>
                    </div>
                    <span className="tw-rounded-full tw-bg-emerald-50 tw-px-2.5 tw-py-1 tw-text-[8px] tw-font-semibold tw-text-[#16836F]">
                        Баримт
                    </span>
                </div>

                <div className="tw-my-4 tw-h-px tw-bg-slate-100" />

                <span className="tw-block tw-text-[10px] tw-font-semibold tw-text-slate-900">Баримтын төрөл</span>
                <p className="tw-mt-1 tw-mb-0 tw-text-[9px] tw-leading-4 tw-text-slate-400">
                    Хувь хүн эсвэл байгууллагын баримтаа сонгоно уу
                </p>

                <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-2 tw-rounded-[14px] tw-bg-[#F3F5F7] tw-p-1.5">
                    <button
                        type="button"
                        onClick={() => { setIsOrganization(false); setRegNo(""); setOrgName(""); setOrgError(""); }}
                        className={`tw-h-10 tw-rounded-[11px] tw-text-[10px] tw-font-semibold tw-transition-all ${
                            !isOrganization
                                ? 'tw-bg-[#0B1220] tw-text-white tw-shadow-[0_2px_8px_rgba(15,23,42,0.10)]'
                                : 'tw-text-slate-500 hover:tw-text-slate-700'
                        }`}
                    >
                        Хувь хүн
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsOrganization(true)}
                        className={`tw-h-10 tw-rounded-[11px] tw-text-[10px] tw-font-semibold tw-transition-all ${
                            isOrganization
                                ? 'tw-bg-[#0B1220] tw-text-white tw-shadow-[0_2px_8px_rgba(15,23,42,0.10)]'
                                : 'tw-text-slate-500 hover:tw-text-slate-700'
                        }`}
                    >
                        Байгууллага
                    </button>
                </div>

                {isOrganization && (
                    <div className="tw-mt-4">
                        <span className="tw-mb-1.5 tw-block tw-text-[9px] tw-font-medium tw-text-slate-500">Байгууллагын регистр</span>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-[13px] tw-border tw-border-slate-200 tw-bg-[#F8FAFC] tw-p-1.5 focus-within:tw-border-[#65B7A5] focus-within:tw-bg-white">
                            <span className="tw-pl-2 tw-text-slate-400"><FiSearch size={16} /></span>
                            <input
                                type="text"
                                placeholder="Регистрийн дугаар"
                                value={regNo}
                                onChange={(e) => {
                                    setRegNo(e.target.value.toUpperCase());
                                    if (orgName || orgError) { setOrgName(""); setOrgError(""); }
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && checkOrgName()}
                                className="tw-h-9 tw-min-w-0 tw-flex-1 tw-bg-transparent tw-px-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.05em] tw-text-slate-800 tw-outline-none placeholder:tw-font-normal placeholder:tw-tracking-normal placeholder:tw-text-slate-400"
                            />
                            <button
                                type="button"
                                onClick={checkOrgName}
                                disabled={isFetchingOrg}
                                className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-[#0B1220] tw-text-white tw-transition hover:tw-bg-[#162033] disabled:tw-opacity-50"
                            >
                                {isFetchingOrg
                                    ? <span className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-white/30 tw-border-t-white" />
                                    : <FiSearch size={15} />}
                            </button>
                        </div>

                        {(isFetchingOrg || orgName || orgError) && (
                            <div className={`tw-mt-2.5 tw-flex tw-items-center tw-gap-2.5 tw-rounded-[12px] tw-border tw-px-3 tw-py-2.5 ${
                                orgError ? 'tw-border-rose-200 tw-bg-rose-50' : 'tw-border-emerald-200 tw-bg-emerald-50'
                            }`}>
                                {isFetchingOrg
                                    ? <span className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-slate-300 tw-border-t-[#16836F]" />
                                    : orgError
                                        ? <FiAlertCircle className="tw-text-rose-500" size={16} />
                                        : <FiCheckCircle className="tw-text-[#16836F]" size={16} />}
                                <span className={`tw-text-[9.5px] tw-font-medium tw-leading-4 ${
                                    orgError ? 'tw-text-rose-700' : isFetchingOrg ? 'tw-text-slate-500' : 'tw-text-emerald-700'
                                }`}>
                                    {isFetchingOrg ? 'Татварын системээс шалгаж байна...' : orgError ? orgError : orgName}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={getEbarimt}
                className="tw-mt-3 tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-[14px] tw-bg-[#0B1220] tw-text-[11px] tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#162033] active:tw-scale-[0.99]"
            >
                <FiFileText size={16} />
                Баримт үүсгэх
            </button>

            <button
                type="button"
                onClick={() => navigate(-1)}
                className="tw-mt-2.5 tw-flex tw-h-11 tw-w-full tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-[13px] tw-border tw-border-slate-200 tw-bg-white tw-text-[11px] tw-font-semibold tw-text-slate-600 tw-transition hover:tw-bg-slate-50 active:tw-scale-[0.99]"
            >
                <FiChevronLeft size={15} />
                Буцах
            </button>

            <Footer />
        </div>
    );

    const renderReceipt = () => (
        <div className="tw-w-full">
            <div className="tw-rounded-[19px] tw-border tw-border-slate-200/90 tw-bg-white tw-p-4 min-[390px]:tw-p-[18px]">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-50 tw-text-[#16836F]">
                            <FiCheckCircle size={20} />
                        </div>
                        <div>
                            <span className="tw-block tw-text-[12px] tw-font-semibold tw-text-slate-900">Амжилттай</span>
                            <span className="tw-mt-0.5 tw-block tw-text-[8.5px] tw-text-slate-400">Ebarimt үүсгэгдлээ</span>
                        </div>
                    </div>
                    <span className="tw-rounded-full tw-bg-emerald-50 tw-px-2.5 tw-py-1 tw-text-[8px] tw-font-semibold tw-text-[#16836F]">Бэлэн</span>
                </div>

                <div className="tw-my-4 tw-h-px tw-bg-slate-100" />

                <div className="tw-space-y-3">
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <span className="tw-text-[9.5px] tw-text-slate-400">Салбар</span>
                        <span className="tw-max-w-[220px] tw-truncate tw-text-right tw-text-[10px] tw-font-semibold tw-text-slate-800">
                            {title || "iParking"}
                        </span>
                    </div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <span className="tw-text-[9.5px] tw-text-slate-400">Төрөл</span>
                        <span className="tw-text-[10px] tw-font-semibold tw-text-slate-800">
                            {isOrganization ? "Байгууллага" : "Хувь хүн"}
                        </span>
                    </div>

                    {isOrganization && regNo && (
                        <>
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                                <span className="tw-text-[9.5px] tw-text-slate-400">Байгууллага</span>
                                <span className="tw-max-w-[220px] tw-truncate tw-text-right tw-text-[10px] tw-font-semibold tw-text-slate-800">{orgName}</span>
                            </div>
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                                <span className="tw-text-[9.5px] tw-text-slate-400">РД</span>
                                <span className="tw-text-[10px] tw-font-semibold tw-text-slate-800">{regNo}</span>
                            </div>
                        </>
                    )}

                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <span className="tw-text-[9.5px] tw-text-slate-400">Огноо</span>
                        <span className="tw-text-right tw-text-[10px] tw-font-medium tw-text-slate-700">{receiptData.date}</span>
                    </div>
                </div>

                <div className="tw-my-4 tw-h-px tw-bg-slate-100" />

                <div className="tw-flex tw-items-end tw-justify-between tw-gap-4">
                    <span className="tw-text-[10px] tw-text-slate-400">Мөнгөн дүн</span>
                    <span className="tw-text-[28px] tw-font-semibold tw-tracking-[-0.04em] tw-text-[#101828]">
                        {formatPrice(serverAmount ?? price)}
                    </span>
                </div>

                {receiptData.qrData && (
                    <div className="tw-mt-4 tw-flex tw-flex-col tw-items-center tw-rounded-[16px] tw-border tw-border-slate-200 tw-bg-[#F8FAFC] tw-p-4">
                        <span className="tw-mb-3 tw-text-[8.5px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-400">Баримтын QR</span>
                        <div className="tw-rounded-[14px] tw-border tw-border-slate-200 tw-bg-white tw-p-2.5">
                            <QRCodeCanvas value={receiptData.qrData} size={170} />
                        </div>
                    </div>
                )}

                {receiptData.lottery && (
                    <div className="tw-mt-3 tw-rounded-[13px] tw-border tw-border-emerald-100 tw-bg-emerald-50/70 tw-px-3 tw-py-3 tw-text-center">
                        <span className="tw-block tw-text-[8px] tw-font-semibold tw-uppercase tw-tracking-[0.1em] tw-text-[#16836F]">Сугалааны дугаар</span>
                        <span className="tw-mt-1.5 tw-block tw-text-[19px] tw-font-bold tw-tracking-[0.16em] tw-text-slate-900">{receiptData.lottery}</span>
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={() => navigate(-1)}
                className="tw-mt-3 tw-h-11 tw-w-full tw-rounded-[13px] tw-border tw-border-slate-200 tw-bg-white tw-text-[11px] tw-font-semibold tw-text-slate-600 tw-transition hover:tw-bg-slate-50 active:tw-scale-[0.99]"
            >
                Дуусгах
            </button>

            <Footer />
        </div>
    );

    return (
        <div
            className="tw-fixed tw-inset-0 tw-z-[99999] tw-overflow-y-auto tw-overscroll-contain tw-bg-[#EEF1F4]"
            style={{
                fontFamily: UI_FONT,
                backgroundImage: 'radial-gradient(circle at 50% -10%, rgba(255,255,255,0.95) 0%, rgba(238,241,244,0) 48%)'
            }}
        >
            {toastMsg && (
                <div className="tw-fixed tw-left-1/2 tw-top-5 tw-z-[100000] tw-w-[calc(100%-24px)] tw-max-w-[380px] tw--translate-x-1/2 tw-rounded-[13px] tw-bg-[#0B1220] tw-px-4 tw-py-3 tw-text-center tw-text-[10px] tw-font-semibold tw-text-white tw-shadow-[0_12px_30px_rgba(15,23,42,0.22)]">
                    {toastMsg}
                </div>
            )}

            <div className="tw-min-h-full tw-w-full tw-px-3 tw-py-3.5 sm:tw-flex sm:tw-items-center sm:tw-justify-center sm:tw-px-5 sm:tw-py-7">
                <main className="tw-mx-auto tw-w-full tw-max-w-[420px]">
                    <section className="tw-overflow-hidden tw-rounded-[26px] tw-border tw-border-white/80 tw-bg-[#F8FAFC] tw-shadow-[0_20px_52px_rgba(15,23,42,0.10)]">
                        <div className="tw-h-[2px] tw-w-full tw-bg-[#16836F]" />

                        <Header
                            badge={
                                isReceiptGenerated
                                    ? 'Амжилттай'
                                    : isLoading
                                        ? 'Ачаалж байна'
                                        : 'И-Баримт'
                            }
                        />

                        <div className="tw-p-3.5 min-[390px]:tw-p-[18px]">
                            {isLoading
                                ? renderLoading()
                                : hasError
                                    ? renderError()
                                    : isReceiptGenerated
                                        ? renderReceipt()
                                        : renderSelection()}
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}
